import { useCallback, useEffect, useMemo, useState } from 'react';

import axios from 'axios';

import { url } from 'constants/index';

interface ApiResponse<T> {
  totalItems: number;
  items: T[];
}

const PAGE_SIZE = 200;

export const useIngestionSources = <T = any, >(sport?: number, shouldFetch = true) => {
  const [ingestionSources, setIngestionSources] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchIngestionSources = useCallback(async () => {
    if (!shouldFetch) {
      return;
    }

    setIsLoading(true);

    try {
      const buildUrl = (page: number) =>
        `${url.user_access}?pageSize=${PAGE_SIZE}&page=${page}&filter=isIngestionSource[equals]:true${
          sport !== undefined && sport !== null ? `&sport=${sport}` : ''
        }`;

      const initialResponse = await axios.get<ApiResponse<T>>(buildUrl(1));

      if (!Array.isArray(initialResponse.data?.items)) {
        setIngestionSources([]);
        return;
      }

      const totalPages = Math.ceil((initialResponse.data.totalItems || 0) / PAGE_SIZE);
      let allSources = initialResponse.data.items;

      if (totalPages > 1) {
        const pagesArray = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
        const allRequests = pagesArray.map(page => axios.get<ApiResponse<T>>(buildUrl(page)));
        const responses = await Promise.all(allRequests);

        allSources = allSources.concat(responses.flatMap(response => response.data.items || []));
      }

      setIngestionSources(allSources);
    } catch (error) {
      console.error('Failed to fetch ingestion sources:', error);
      setIngestionSources([]);
    } finally {
      setIsLoading(false);
    }
  }, [shouldFetch, sport]);

  useEffect(() => {
    fetchIngestionSources();
  }, [fetchIngestionSources]);

  const ingestionSourceOptions = useMemo(() => {
    const sourceByName = new Map<string, { username: string; priority: number }>();

    (ingestionSources as Array<{ username?: string; priority?: number }>).forEach(source => {
      if (!source?.username) {
        return;
      }

      const nextPriority = Number.isFinite(source.priority)
        ? Number(source.priority)
        : Number.MAX_SAFE_INTEGER;
      const existingSource = sourceByName.get(source.username);

      if (!existingSource || nextPriority < existingSource.priority) {
        sourceByName.set(source.username, { username: source.username, priority: nextPriority });
      }
    });

    return Array.from(sourceByName.values())
      .sort((a, b) => a.priority - b.priority || a.username.localeCompare(b.username))
      .map(s => s.username);
  }, [ingestionSources]);

  return {
    ingestionSources,
    ingestionSourceOptions,
    isLoading,
    fetchIngestionSources,
  };
};
